import React from 'react'
import styles from './styles.module.css'
import RadioButtonOrCheckbox from '../RadioButtonOrCheckbox'
import Text from '../../../../Text'
import { $red60 } from '../../../../../styles/colors/primitives'
import { formatPrice } from '../../../../../utils/formats'

/**
 * A reusable selectable item component for product configurators.
 * Handles selected/disabled states, checkbox visibility, and "Out of stock" text.
 *
 * @param {Object} props
 * @param {boolean} props.selected - Whether the item is selected
 * @param {boolean} props.disabled - Whether the item is disabled (e.g., out of stock)
 * @param {function} props.onClick - Click handler
 * @param {React.ReactNode} props.image - Image element to display
 * @param {string} props.name - Item name
 * @param {Object} props.price - Price object with priceCents and currency (the price paid)
 * @param {Object} props.regularPrice - Optional original price; shown struck-through when it differs from price
 * @param {string} props.disabledText - Text to show when disabled (default: "Out of stock")
 * @param {React.ReactNode} props.children - Additional content to render in the info section
 * @param {boolean} props.checkboxStyle - Use checkbox style instead of radio (default: true)
 */
function SelectableItem({
  selected,
  disabled,
  onClick,
  image,
  name,
  price,
  regularPrice,
  disabledText = 'Out of stock',
  children,
  checkboxStyle = true
}) {
  const cx = (...c) => c.filter(Boolean).join(' ')

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
    }
  }

  const hasDiscount = !!price && !!regularPrice && regularPrice.priceCents !== price.priceCents

  return (
    <div
      className={cx(styles.wrapper, selected && styles.wrapperSelected, disabled && styles.wrapperDisabled)}
      onClick={handleClick}
    >
      {!disabled && (
        <RadioButtonOrCheckbox
          isSelected={selected}
          checkboxStyle={checkboxStyle}
          checkedColor={$red60}
        />
      )}
      <div className={styles.contentWrapper}>
        {image}
        <div className={styles.infoWrapper}>
          <Text type="small">{name}</Text>
          {disabled && <Text type="tiny">{disabledText}</Text>}
          {children}
        </div>
      </div>
      {price && (
        <div className={styles.priceWrapper}>
          {hasDiscount && (
            <span className={styles.regularPriceStrike}>{formatPrice(regularPrice)}</span>
          )}
          <Text type="heading-8" color={hasDiscount ? '#DE1010' : undefined}>{formatPrice(price)}</Text>
        </div>
      )}
    </div>
  )
}

export default SelectableItem
